import {Box} from '@mantine/core'
import {Fragment} from 'react'

import {WORKSPACE_SWIM_LANE_BOARD_SURFACE} from '../dispatch/workspace-surfaces'
import {DispatchBoardLaneEmptyMessage} from './board-empty-state'
import {CleanUpSection} from './clean-up-section'
import {LaneRiskSubheader, laneRiskSubheaderClasses} from './lane-risk-subheader'
import {groupByRiskWindow} from './risk-window-groups'
import {resolveNowHour} from './schedule-urgency'
import {stickyClasses} from './swim-lane-sticky'
import classes from './widescreen-board.module.css'
import {
    BoardCardFromEntry,
    LANE_ORDER,
    LaneHeading,
    pressEntry,
    useWidescreenBoard,
    type WidescreenBoardProps,
} from './widescreen-board-common'

/**
 * Widescreen card layout: operational cards. Lanes stay stacked so the reading
 * order (needs action → notify drivers → dispatched) is preserved, but each lane fans its
 * jobs into a responsive card grid that fills the extra horizontal space.
 */
export function DispatchCardBoard({
    data,
    viewRange,
    onJobPress,
    onCommittedPress,
    highlightAssignmentId = null,
}: WidescreenBoardProps) {
    const {sections, cleanUp} = useWidescreenBoard(data, viewRange)
    const visibleDays = data.visibleDays ?? [data.scheduleDate]
    const cardHorizon = {
        visibleDays,
        viewRange,
        nowHour: resolveNowHour(),
    }

    return (
        <Box className={classes.boardShell} style={WORKSPACE_SWIM_LANE_BOARD_SURFACE}>
            <div className={stickyClasses.laneScrollBody}>
                {LANE_ORDER.map(({lane, key}) => {
                    const entries = sections[key]
                    return (
                        <Fragment key={lane}>
                            <LaneHeading lane={lane} count={entries.length} />
                            <div className={classes.laneBody}>
                                {entries.length > 0 ? (
                                    groupByRiskWindow(entries, viewRange, visibleDays).map(
                                        (group) => (
                                            <div
                                                key={group.label ?? 'all'}
                                                className={laneRiskSubheaderClasses.group}
                                            >
                                                {group.label && (
                                                    <LaneRiskSubheader
                                                        label={group.label}
                                                        count={group.entries.length}
                                                    />
                                                )}
                                                <div className={classes.laneGrid}>
                                                    {group.entries.map((entry) => (
                                                        <BoardCardFromEntry
                                                            key={`${entry.job.id}-${entry.viewDayOffset}`}
                                                            entry={entry}
                                                            variant="card"
                                                            highlighted={
                                                                entry.job.id ===
                                                                highlightAssignmentId
                                                            }
                                                            horizon={cardHorizon}
                                                            onPress={() =>
                                                                pressEntry(
                                                                    entry,
                                                                    onJobPress,
                                                                    onCommittedPress,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ),
                                    )
                                ) : (
                                    <DispatchBoardLaneEmptyMessage lane={lane} />
                                )}
                            </div>
                        </Fragment>
                    )
                })}
                <CleanUpSection count={cleanUp.length}>
                    <div className={classes['laneGrid']}>
                        {cleanUp.map((entry) => (
                            <BoardCardFromEntry
                                key={entry.job.id}
                                entry={entry}
                                variant="card"
                                highlighted={entry.job.id === highlightAssignmentId}
                                horizon={cardHorizon}
                                onPress={() => pressEntry(entry, onJobPress, onCommittedPress)}
                            />
                        ))}
                    </div>
                </CleanUpSection>
            </div>
        </Box>
    )
}
